/**
 * FAQs shown on public pages, as plain text: the page renders them, and the
 * same list becomes that page's FAQPage structured data (src/content/seo), so
 * the structured data only ever repeats questions a visitor can see.
 */
import { SUPPORT_PHONE_DISPLAY } from '../constants/support.js';
import { PLAN_MIN_DAYS } from './help/tokens.js';

/** The homepage's "Frequently asked questions". */
export const HOME_FAQS = [
  {
    q: "Is there really no credit check?",
    a: "Yes. There's no credit check—everyone is approved."
  },
  {
    q: "Is my trip price secured when I book?",
    a: "Yes. With Defera Installments, your deposit secures your agreed-upon trip price right away. With Defera Zero (refundable fares), your trip price is protected with $0 down — nothing due until your first scheduled installment date. Once your deposit is received, your trip price is protected from future airfare increases while you remain in good standing on your payment plan."
  },
  {
    q: "When do I receive my ticket?",
    a: "Your airline confirmation number and e-ticket are released once your installment plan is paid in full. Defera may complete booking or ticket issuance at different times while conducting verification or other operational processes — but your price is locked from the moment you confirm, so you're protected while you pay."
  },
  {
    q: "How far in advance do I need to book?",
    a: `At least ${PLAN_MIN_DAYS} days before departure. That gives you enough time to pay off your balance before you fly.`
  },
  {
    q: "What's the difference between Installments and Zero?",
    a: "Installments is our standard plan — pay a deposit upfront to secure your trip price, balance split over time. Zero is 0% down on refundable fares (United, American, Southwest, JetBlue, Virgin Atlantic) — your trip price is protected at booking, nothing due at checkout, first payment on your first installment date, 75% back if you cancel 24+ hrs out. Both: no credit check, no interest."
  },
  {
    q: "What happens if I miss a payment?",
    a: "We'll send reminders before each payment is due. If you're having trouble, chat with us or send us a message from our Contact page, and we'll work with you to find a solution. Please note: defaulting may result in referral to a third-party collection agency."
  },
  {
    q: "Can I pay off my balance early?",
    a: "Absolutely. You can pay off your remaining balance anytime—no penalties, no extra fees. We encourage it."
  },
  {
    q: "Can I choose my seat?",
    a: "Yes. On many airlines you'll see a live, interactive seat map during checkout — pick your exact seat from the real cabin layout, with prices that match your fare. Seat costs selected at booking can even roll into your installment plan. No seat map for your flight? Set a preference and our concierge will seat your group together, as close to the front as your fare allows. You can also purchase seats after booking, right from your trip page — paid in full, one time. If your fare already includes seat selection, there's nothing to buy: our team picks your seats for you at no cost."
  },
  {
    q: "When will I see my seat numbers?",
    a: "Airlines with a live seat map let you see your seat assignments right away: seats you pick on it at checkout show in your account as soon as you book. Otherwise, our team picks your seats for you (free, together and by the window when your fare includes seat selection), and your seat numbers are released later in your trip. There's no way to get them sooner, and asking again doesn't change the timing."
  },
  {
    q: "Is booking with Defera different from buying from the airline?",
    a: "For you, it works like any other ticket. Your trip price is locked with Defera, we take care of everything with the airline, and you're never at a disadvantage compared with someone who bought the same flight at retail. Behind the scenes, a Defera trip is processed differently: our systems work with partners, intermediaries and airlines in an automated, orchestrated way, with a lot of moving parts. That's why some things, like seat numbers our team picks and your airline confirmation number, come later than they would on a retail booking."
  },
  {
    q: "If I change my mind, can I get a refund on payments made?",
    a: "For Defera Zero: cancel up to 24 hours before departure and receive 75% of payments back. For other bookings, we follow the airline's policy. If the airline allows refunds, we do too. If the fare is non-refundable with the airline, it's non-refundable through us."
  }
];

/** How It Works: "Twelve real questions we get from new travelers". */
export const HOW_IT_WORKS_FAQS = [
  {
    q: 'Will using Defera affect my credit score?',
    a: "Nope. We don't run credit checks, and we don't report to credit bureaus. Whether you pay early, on time, or late — none of it touches your credit score. Ever.",
  },
  {
    q: 'When exactly is my fare locked in?',
    a: "The instant your booking is approved at checkout. From that moment, your agreed-upon trip price is secured — protected from future airfare increases while you stay in good standing on your payment plan.",
  },
  {
    q: "Why does my confirmation number come after my final payment?",
    a: "Your trip price is secured the moment your booking is approved, and your airline confirmation number is released once your trip is paid in full. That's what lets us offer 0% interest with no credit check. Want it sooner? Pay ahead or pay off early any time.",
  },
  {
    q: 'Can I change my flight after I book?',
    a: "Yes, for new dates or times on the same route. On eligible fares with major airlines, use Change Flight in your trip's Flight Options to pick the new flight yourself; otherwise, Request Flight Change sends the request to our team. A different departure city or destination is a new trip: cancel and rebook.",
  },
  {
    q: 'What payment methods do you accept?',
    a: 'Debit and credit cards. Bank transfers, PayPal and digital wallets aren’t offered, and prepaid cards aren’t accepted on payment plans.',
  },
  {
    q: 'What if my card expires mid-plan?',
    a: "Swap in your new card any time: open the trip in Trips and tap Update Card in its Payment Schedule. Every future payment uses the new card, and the schedule keeps running uninterrupted.",
  },
  {
    q: 'Is my payment information secure?',
    a: "Yes. We never store your full card details: payments are processed through certified PCI-DSS payment gateways that tokenize your card, and everything travels over TLS encryption.",
  },
  {
    q: 'Is there a maximum booking amount?',
    a: "No hard cap. The longer the runway between booking and travel, the larger the trip we can support. Most travelers book 4–24 weeks out and split fares from $300 to $5,000+.",
  },
  {
    q: 'Can I have multiple trips going at once?',
    a: "Yes — book as many as you want. There's no limit on the number of trips you can have on Defera at the same time. Each trip runs on its own independent payment schedule, all visible side by side in your dashboard. Plenty of our travelers stack a summer trip, a holiday trip, and a weekend getaway in parallel.",
  },
  {
    q: 'Can I add bags, seats, or upgrades?',
    a: "Yes. Seats you choose at checkout can roll into your installment plan. After booking, open the trip's Flight Options to Purchase Seats or Purchase Baggage on supported airlines; those are paid by card in one go, separately from your plan.",
  },
  {
    q: 'Where can I see my payment schedule?',
    a: "Shown at checkout before you confirm, then always visible in your Defera dashboard. We also email you a reminder 48 hours before every charge so nothing surprises you.",
  },
  {
    q: 'Is Defera available outside the U.S.?',
    a: "U.S. only for now. The U.K. and EU are next on the roadmap (mid–late 2026). Sign up for the waitlist if you're outside the U.S. and we'll let you know when we launch in your region.",
  },
  {
    q: 'How do I reach customer support?',
    a: `Chat with the Defera Concierge any time, call ${SUPPORT_PHONE_DISPLAY} (answered 24/7), or send our team a message from the Contact page (we reply within 24 hours). Urgent travel issues go straight to our team.`,
  },
];
